import {
  MeetingCreationOutcome,
  MeetingCreationResult,
  MeetingCreationSummary,
} from '../models/meeting-creation-result.model';
import { NotificationService } from '../services/notification.service';

/** Farbwerte von PrimeNGs p-tag. */
export type TagSeverity = 'success' | 'info' | 'warning' | 'danger';

const OUTCOME_LABELS: Record<MeetingCreationOutcome, string> = {
  Created: 'Erstellt',
  AlreadyExists: 'Bereits vorhanden',
  SkippedNoUser: 'Kein Benutzer',
  SkippedNoTemplate: 'Keine Vorlage',
  Failed: 'Fehlgeschlagen',
  Updated: 'Geändert',
  Deleted: 'Gelöscht',
};

export function meetingCreationOutcomeLabel(
  outcome: MeetingCreationOutcome,
): string {
  return OUTCOME_LABELS[outcome] ?? outcome;
}

export function meetingCreationOutcomeSeverity(
  outcome: MeetingCreationOutcome,
): TagSeverity {
  switch (outcome) {
    case 'Created':
    case 'Updated':
    case 'Deleted':
      return 'success';
    case 'AlreadyExists':
      return 'info';
    case 'Failed':
      return 'danger';
    default:
      return 'warning';
  }
}

/** z.B. "4 Meetings erstellt, 2 bereits vorhanden, 1 fehlgeschlagen". */
export function describeMeetingCreation(summary: MeetingCreationSummary): string {
  const parts: string[] = [];

  if (summary.created > 0) {
    parts.push(
      summary.created === 1
        ? '1 Meeting erstellt'
        : `${summary.created} Meetings erstellt`,
    );
  }
  if (summary.alreadyExists > 0) {
    parts.push(`${summary.alreadyExists} bereits vorhanden`);
  }

  const skipped = summary.skippedNoUser + summary.skippedNoTemplate;
  if (skipped > 0) {
    parts.push(`${skipped} übersprungen`);
  }
  if (summary.failed > 0) {
    parts.push(`${summary.failed} fehlgeschlagen`);
  }

  return parts.length > 0 ? parts.join(', ') : 'Es gab nichts zu tun.';
}

/** Ein Lauf ohne Erstellungen ist kein Erfolg, aber auch kein Fehler. */
export function meetingCreationSeverity(
  summary: MeetingCreationSummary,
): 'success' | 'warn' | 'error' {
  if (summary.failed > 0) return 'error';
  if (summary.created === 0) return 'warn';
  return 'success';
}

export function notifyMeetingCreationResult(
  notificationService: NotificationService,
  result: MeetingCreationResult,
): void {
  const message = describeMeetingCreation(result.summary);

  switch (meetingCreationSeverity(result.summary)) {
    case 'error':
      notificationService.error(message, result.items);
      break;
    case 'warn':
      notificationService.warn(message, result.items);
      break;
    default:
      notificationService.success(message, result.items);
  }
}
